import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  const filteredArticles = useMemo(() => {
    if (!activeSlug || activeSlug === 'all') return articles;
    return articles.filter(a => a.categorySlug === activeSlug);
  }, [activeSlug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);

  return (
    <>
      <SEOHead
        title={currentCategory ? `${currentCategory.name} - po文推荐` : '全部文章 - po文推荐'}
        description={currentCategory
          ? `浏览${currentCategory.name}相关的高质量po文推荐，共${currentCategory.count}篇精选内容，涵盖热门话题与达人心水之选。`
          : '浏览全部po文推荐清单，涵盖内容创作、写作技巧、阅读方法等多个领域的高质量精选文章。'}
        canonicalUrl={`https://powen-tuijian.com/category/${activeSlug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '全部文章' },
              ]}
            />

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory?.name || '全部文章'}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {activeSlug === 'all'
                  ? `共 ${articles.length} 篇精选内容，涵盖多个热门分类`
                  : `共 ${currentCategory?.count || 0} 篇${currentCategory?.name}相关精选内容`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 border-b border-background-200/70 pb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveSlug(cat.slug);
                    window.history.pushState({}, '', `/category/${cat.slug}`);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer no-underline"
                >
                  <div className="relative overflow-hidden h-48">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - po文推荐`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {article.isFeatured && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-accent-500/90 text-background-50 text-xs font-medium rounded-full">
                          精选
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-accent-600 font-medium">{article.category}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400">{article.readTime} 分钟</span>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full overflow-hidden bg-background-200">
                          <img src={article.author.avatar} alt={article.author.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-xs text-foreground-500">{article.author.name}</span>
                      </div>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}